// src/components/resources/glossary/GlossaryGrid.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { motion } from 'motion/react'
import { useState, useMemo, useEffect, useCallback, useDeferredValue } from 'react'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import { rankGlossaryTerms } from '~/lib/resources/glossary/search'
import { getGlossaryCategoryTheme } from '~/lib/resources/glossary/category-ui'
import { glossaryTermHref } from '~/lib/resources/resource-path'
import { cn } from '~/lib/utils'

// Slim shape that matches what the grid actually renders
type TermCardLike = Pick<
  GlossaryTerm,
  'slug' | 'title' | 'shortDescription' | 'category' | 'relatedTerms' | 'definition' | 'acronym'
> & {
  // include this for platform pills
  platformName?: string[]
}


interface GlossaryGridProps {
  terms: TermCardLike[]
  relatedIndex?: Record<string, GlossaryCategory>
  limit?: number
  showSearch?: boolean
  showViewAll?: boolean
  category: string
  gridId?: string // Add unique identifier for collision safety
  // Optional props to avoid unnecessary API calls
  categoryCounts?: Record<string, number> // Pass counts from server to avoid API calls
}

interface GlossaryCountsResponse {
  counts: Record<string, number>
}

interface GlossaryCategoryResponse {
  data?: TermCardLike[]
}

// Create a separate component for the term card to better manage complexity
function GlossaryTermCard({ 
  term, 
  relatedIndex,
  gridId = 'default'
}: { 
  term: TermCardLike 
  relatedIndex?: Record<string, GlossaryCategory>
  gridId?: string
}): JSX.Element {
  const theme = getGlossaryCategoryTheme(term.category)
  const Icon = theme.icon

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border-2 border-gray-200",
        "bg-white p-6 transition-all hover:shadow-lg hover:-translate-y-1",
        "dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800"
      )}
    >
      <Link
        href={glossaryTermHref(term)}
        className="absolute inset-0 z-10"
        aria-label={`Learn more about ${term.title}`}
      >
        <span className="sr-only">Learn more about {term.title}</span>
      </Link>

      {/* Category Icon */}
      <div className={cn(
        "absolute right-6 top-6 rounded-xl bg-gradient-to-br p-2 transition-all",
        theme.subtleGradient
      )}>
        <Icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
      </div>

      {/* Platform Badges */}
      {term.category === 'platform' && term.platformName?.length && (
        <div className="absolute left-6 top-6 flex flex-wrap gap-2">
          {term.platformName.map((platform: string) => (
            <div
              key={`${gridId}-${term.slug}-platform-${platform}`}
              className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 
                      text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 
                      dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-400/20 
                      transition-colors"
            >
              {platform}
            </div>
          ))}
        </div>
      )}

      {/* Term Content */}
      <div className="flex flex-grow flex-col space-y-3 mt-8">
        <h3 className="text-xl font-semibold text-gray-900 group-hover:text-indigo-600 
                     dark:text-white dark:group-hover:text-indigo-400 line-clamp-2">
          {term.title}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-3 dark:text-gray-400">
          {term.shortDescription}
        </p>
      </div>

      {/* Related Terms */}
      {term.relatedTerms && term.relatedTerms.length > 0 && (
        <div className="relative z-20 mt-4 flex flex-wrap gap-2">
          {term.relatedTerms.slice(0, 2).map((relatedTerm) => {
            const pillCategory = relatedIndex?.[relatedTerm.slug] ?? term.category
            
            return (
              <Link
                key={`${gridId}-${term.slug}-related-${relatedTerm.slug}`}
                href={glossaryTermHref({ category: pillCategory, slug: relatedTerm.slug })}
                className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 
                         text-xs font-medium text-gray-600 hover:bg-gray-100 
                         dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 
                         transition-colors relative z-20"
              >
                {relatedTerm.title}
              </Link>
            )
          })}
          {term.relatedTerms.length > 2 && (
            <div className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 
                         text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              +{term.relatedTerms.length - 2} more
            </div>
          )}
        </div>
      )}

      {/* Learn More Link */}
      <div className="mt-6 flex items-center text-sm font-medium text-indigo-600 
                    dark:text-indigo-400 border-t border-gray-100 dark:border-gray-800 pt-4">
        <span className="group-hover:underline">Learn more</span>
        <RiArrowRightLine className="ml-2 h-4 w-4 transition-transform 
                                  group-hover:translate-x-1" />
      </div>
    </div>
  )
}

export function GlossaryGrid({
  terms: initialTerms,
  relatedIndex,
  limit = 18,
  showSearch = true,
  showViewAll = true,
  category,
  gridId = 'default',
  categoryCounts: serverCounts
}: GlossaryGridProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [hasInteractedWithSearch, setHasInteractedWithSearch] = useState(false)

  const normalizedCategory = useMemo(() => category?.toLowerCase?.() ?? '', [category])

  const {
    data: countsData,
    isError: isCountsError,
    error: countsError
  } = useQuery<GlossaryCountsResponse>({
    queryKey: ['glossary', 'counts'],
    queryFn: async ({ signal }) => {
      const response = await fetch('/api/glossary?counts=true', { signal })
      if (!response.ok) {
        throw new Error('Failed to fetch glossary counts')
      }
      return response.json() as Promise<GlossaryCountsResponse>
    },
    enabled: !serverCounts,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    retry: 1
  })

  const shouldLoadAllTerms = showSearch && Boolean(normalizedCategory) && hasInteractedWithSearch

  const {
    data: fetchedCategoryTerms,
    isFetching: isFetchingAllTerms,
    isError: isCategoryTermsError,
    error: categoryTermsError
  } = useQuery<GlossaryCategoryResponse>({
    queryKey: ['glossary', 'category', normalizedCategory, 'terms'],
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/glossary?category=${encodeURIComponent(normalizedCategory)}`, { signal })
      if (!response.ok) {
        throw new Error('Failed to fetch additional glossary terms')
      }
      return response.json() as Promise<GlossaryCategoryResponse>
    },
    enabled: shouldLoadAllTerms,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    retry: 1
  })

  const resolvedCategoryCounts = useMemo(
    () => serverCounts ?? countsData?.counts ?? {},
    [serverCounts, countsData?.counts]
  )

  const categoryTerms = useMemo(() => {
    const baseTerms = initialTerms ?? []
    const fetchedTerms = fetchedCategoryTerms?.data ?? []
    if (!fetchedTerms.length) {
      return baseTerms
    }

    const existingSlugs = new Set(baseTerms.map(term => term.slug))
    const deduped = fetchedTerms.filter(term => !existingSlugs.has(term.slug))
    return [...baseTerms, ...deduped]
  }, [initialTerms, fetchedCategoryTerms?.data])

  useEffect(() => {
    if (isCountsError) {
      logClientError(countsError, { component: 'GlossaryGrid', context: 'Failed to fetch glossary counts' })
    }
  }, [isCountsError, countsError])

  useEffect(() => {
    if (isCategoryTermsError) {
      logClientError(categoryTermsError, { component: 'GlossaryGrid', context: 'Failed to fetch additional glossary terms' })
    }
  }, [isCategoryTermsError, categoryTermsError])
  
  /**
   * Search interaction handlers
   * 
   * These functions manage user interactions with the search functionality:
   * - Track when user first interacts with search (triggers lazy loading)
   * - Handle search query updates
   * - Maintain search state consistency
   */
  const handleSearchFocus = useCallback((): void => {
    if (!hasInteractedWithSearch) {
      setHasInteractedWithSearch(true)
    }
  }, [hasInteractedWithSearch])

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!hasInteractedWithSearch) {
      setHasInteractedWithSearch(true)
    }
    setSearchQuery(event.target.value)
  }, [hasInteractedWithSearch])

  /**
   * Enhanced search filtering with intelligent ranking and comprehensive field coverage
   *
   * This search implementation leverages the shared `rankGlossaryTerms` ranker to provide:
   * - Intelligent scoring and ranking of results
   * - Comprehensive field coverage (title, acronym, description, definition, category, slug)
   * - Word boundary detection for more precise matches
   * - Multi-word query support with bonus scoring
   * - Efficient deduplication and result limiting
   *
   * `useDeferredValue` keeps the input responsive on slow mobile devices
   * by allowing React to skip the expensive scoring pass when new keystrokes
   * arrive in quick succession, which is the main driver of mobile INP here.
   */
  const deferredQuery = useDeferredValue(searchQuery)
  const filteredTerms = useMemo(() => {
    // Shared 14-tier ranker (SoT: `~/lib/resources/glossary/search`). Unlike the
    // hero, the grid shows every category term when the query is empty and does
    // not cap the number of matches.
    const trimmed = deferredQuery.trim()
    return trimmed ? rankGlossaryTerms(categoryTerms, trimmed) : categoryTerms
  }, [categoryTerms, deferredQuery])

  // Memoize displayed terms with proper limit application
  const displayedTerms = useMemo(() => 
    limit ? filteredTerms.slice(0, limit) : filteredTerms,
    [filteredTerms, limit]
  )

  // Calculate if there are more terms to show
  // Use the actual category count from the API, fallback to current terms if not available
  const actualCategoryCount = resolvedCategoryCounts[normalizedCategory] ?? categoryTerms.length
  const hasMore = typeof limit === 'number' ? actualCategoryCount > limit : actualCategoryCount > filteredTerms.length

  const isLoadingMore = isFetchingAllTerms

  return (
    <div className="space-y-8">
      {showSearch && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative max-w-md"
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <RiSearchLine className="h-5 w-5 text-gray-400" />
          </div>
          {isLoadingMore && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
            </div>
          )}
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            className="block w-full rounded-xl border-2 border-gray-200 bg-white/50 py-3 pl-11 pr-4 
                     text-gray-900 placeholder:text-gray-500 focus:bg-white focus:outline-none 
                     focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm
                     dark:border-gray-800 dark:bg-gray-900/50 dark:text-white 
                     dark:placeholder:text-gray-400 dark:focus:bg-gray-900"
            placeholder={`Search ${category ? category + ' ' : ''}terms...`}
          />
        </motion.div>
      )}

      {/* Terms Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {displayedTerms.map((term, index) => (
          <div key={`${gridId}-glossary-term-${term.slug}-${index}`}>
            <GlossaryTermCard term={term} relatedIndex={relatedIndex} gridId={gridId} />
          </div>
        ))}
      </div>

      {/* View All Link */}
      {showViewAll && hasMore && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-12 text-center"
        >
          <Link
            href={`/resources/glossary/${category?.toLowerCase()}`}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-8 py-4",
              "text-base font-semibold text-white shadow-sm",
              "hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2",
              "focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
              "transition-all hover:-translate-y-0.5"
            )}
          >
            View All {category} Terms
            {/* opacity-90 (not -75): the muted count sits on the indigo-600
                button; at opacity-75 the effective #d3d1f9-on-#4f46e5 ratio is
                4.26:1, just under WCAG AA (4.5:1 for this 14px normal text).
                Bumping to opacity-90 raises it to ~5.4:1 while keeping the
                count visually subordinate to the button label. */}
            {actualCategoryCount > 0 && (
              <span className="ml-2 text-sm opacity-90">
                ({actualCategoryCount} total)
              </span>
            )}
            <RiArrowRightLine className="h-5 w-5" />
          </Link>
        </motion.div>
      )}

      {/* Loading indicator for additional terms */}
      {isLoadingMore && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
            Loading complete term set for search...
          </div>
        </motion.div>
      )}
    </div>
  )
} 